import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, brl, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, AlertTriangle } from "lucide-react";
import { Field, inputCls, selectCls, btnPrimary, btnGhost, num } from "@/components/erp/ErpUi";
import { maskCpf, maskCnpj, maskCep, onlyDigits } from "@/components/CustomerProfileFields";
import { useViaCep } from "@/lib/viacep";

const dlg = "bg-zinc-950 border border-zinc-800 text-zinc-100 max-w-2xl max-h-[92dvh] overflow-y-auto";
const maskDoc = (v) => (onlyDigits(v).length > 11 ? maskCnpj(v) : maskCpf(v));

function Lines({ title, rows, total, showNcm }) {
  return (
    <div className="rounded-xl border border-zinc-800 p-3">
      <div className="text-xs font-mono-caps uppercase text-zinc-400 mb-2">{title} · {brl(total)}</div>
      <ul className="space-y-1 text-sm">
        {rows.map((r, i) => (
          <li key={i} className="flex justify-between gap-2">
            <span className="text-zinc-200 min-w-0 break-words">{r.qty}× {r.description}{showNcm && <span className={`ml-1 text-[11px] ${r.ncm ? "text-zinc-500" : "text-red-300"}`}>NCM {r.ncm || "faltando"}</span>}</span>
            <span className="text-zinc-100 whitespace-nowrap">{brl(r.total)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function InvoiceDialog({ open, onOpenChange, order, onDone, source = "orders" }) {
  const [d, setD] = useState(null);
  const [c, setC] = useState({});
  const [desc, setDesc] = useState("");
  const [emitS, setEmitS] = useState(true);
  const [emitP, setEmitP] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setD(null);
    api.get(`/fiscal/${source}/${order.id}/preview`).then(({ data }) => {
      setD(data); setC({ ...data.customer, document: maskDoc(data.customer.document), cep: maskCep(data.customer.cep) });
      setDesc(data.service_description); setEmitS(data.services.length > 0); setEmitP(data.parts.length > 0);
    }).catch((e) => { toast.error(formatApiError(e)); onOpenChange(false); });
  }, [open, order.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useViaCep(c.cep, (d) => setC((p) => ({
    ...p,
    street: d.logradouro || p.street,
    neighborhood: d.bairro || p.neighborhood,
    city: d.localidade || p.city,
    uf: (d.uf || "").toUpperCase() || p.uf,
  })));
  const set = (k, fn = (v) => v) => (e) => setC((p) => ({ ...p, [k]: fn(e.target.value) }));
  const isPj = onlyDigits(c.document).length === 14;
  const partsKind = isPj ? "NF-e" : "NFC-e";
  const emit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/fiscal/${source}/${order.id}/emit`, { customer: { ...c, document: onlyDigits(c.document), cep: onlyDigits(c.cep) }, service_description: desc, emit_service: emitS, emit_parts: emitP, save_customer: true });
      const rej = data.filter((x) => x.status === "rejeitada");
      rej.length ? toast.error(`Nota rejeitada: ${rej[0].reason}`) : toast.success(`${data.length} nota(s) enviada(s) para emissão`);
      onDone(); onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="invoice-dialog" className={dlg}>
        <DialogHeader>
          <DialogTitle className="font-display uppercase">Prévia da nota fiscal</DialogTitle>
          <DialogDescription className="text-zinc-400">Confira e corrija antes de enviar. As correções do cliente ficam salvas no cadastro.</DialogDescription>
        </DialogHeader>
        {!d ? <Loader2 className="h-6 w-6 animate-spin text-green-brand mx-auto" /> : (
          <div className="space-y-4">
            {d.issues.length > 0 && (
              <div data-testid="invoice-issues" className="rounded-xl border border-amber-700 bg-amber-950/30 p-3 text-sm text-amber-200">
                <div className="flex items-center gap-2 font-bold"><AlertTriangle className="h-4 w-4" /> Falta preencher</div>
                <ul className="list-disc ml-5 mt-1">{d.issues.map((x) => <li key={x}>{x}</li>)}</ul>
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
              <Field label="Nome / razão social" className="col-span-2 sm:col-span-4"><Input data-testid="nf-name" value={c.name || ""} onChange={set("name")} className={inputCls} /></Field>
              <Field label="CPF ou CNPJ" className="col-span-2"><Input data-testid="nf-doc" value={c.document || ""} onChange={set("document", maskDoc)} inputMode="numeric" className={inputCls} /></Field>
              <Field label="E-mail" className="col-span-2 sm:col-span-3"><Input data-testid="nf-email" value={c.email || ""} onChange={set("email")} type="email" className={inputCls} /></Field>
              <Field label="Telefone" className="col-span-2 sm:col-span-3"><Input data-testid="nf-phone" value={c.phone || ""} onChange={set("phone", onlyDigits)} inputMode="tel" className={inputCls} /></Field>
              <Field label="CEP" className="col-span-1 sm:col-span-2"><Input data-testid="nf-cep" value={c.cep || ""} onChange={set("cep", maskCep)} inputMode="numeric" className={inputCls} /></Field>
              <Field label="Rua" className="col-span-1 sm:col-span-4"><Input data-testid="nf-street" value={c.street || ""} onChange={set("street")} className={inputCls} /></Field>
              <Field label="Número" className="col-span-1 sm:col-span-2"><Input data-testid="nf-number" value={c.number || ""} onChange={set("number")} className={inputCls} /></Field>
              <Field label="Bairro" className="col-span-1 sm:col-span-4"><Input data-testid="nf-neighborhood" value={c.neighborhood || ""} onChange={set("neighborhood")} className={inputCls} /></Field>
              <Field label="Cidade" className="col-span-1 sm:col-span-4"><Input data-testid="nf-city" value={c.city || ""} onChange={set("city")} className={inputCls} /></Field>
              <Field label="UF" className="col-span-1 sm:col-span-2"><Input data-testid="nf-uf" value={c.uf || ""} onChange={set("uf", (v) => v.toUpperCase().slice(0, 2))} className={inputCls} /></Field>
            </div>
            {d.services.length > 0 && (
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-bold text-zinc-100"><input data-testid="nf-emit-service" type="checkbox" checked={emitS} onChange={(e) => setEmitS(e.target.checked)} /> Emitir NFS-e (serviços)</label>
                <Lines title="Serviços" rows={d.services} total={d.service_total} />
                <Field label="Descrição do serviço na nota"><Textarea data-testid="nf-service-desc" value={desc} onChange={(e) => setDesc(e.target.value)} className="bg-zinc-900 border-zinc-800 text-zinc-100 min-h-[70px]" /></Field>
              </div>
            )}
            {d.parts.length > 0 && (
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-bold text-zinc-100"><input data-testid="nf-emit-parts" type="checkbox" checked={emitP} onChange={(e) => setEmitP(e.target.checked)} /> Emitir {partsKind} (peças) <span className="text-[11px] font-normal text-zinc-500">{isPj ? "cliente com CNPJ" : "cliente com CPF"}</span></label>
                <Lines title="Peças" rows={d.parts} total={d.parts_total} showNcm />
              </div>
            )}
            {!d.services.length && !d.parts.length && <p className="text-sm text-zinc-500">A OS não tem orçamento com itens.</p>}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" className={btnGhost} onClick={() => onOpenChange(false)}>Cancelar</Button>
              <Button data-testid="nf-emit-submit" disabled={busy || (!emitS && !emitP)} onClick={emit} className={btnPrimary}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enviar para emissão"}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function ChargeDialog({ open, onOpenChange, order, onDone, source = "orders" }) {
  const [method, setMethod] = useState("pix");
  const [amount, setAmount] = useState("");
  const [inst, setInst] = useState("1");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setMethod("pix"); setInst("1"); setAmount(String(order?.budget?.total || order?.total || "")); } }, [open, order]);
  const save = async () => {
    setBusy(true);
    try { await api.post(`/fiscal/${source}/${order.id}/charges`, { method, amount: num(amount), installments: Number(inst) || 1 }); toast.success("Cobrança gerada"); onDone(); onOpenChange(false); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="charge-dialog" className="bg-zinc-950 border border-zinc-800 text-zinc-100 max-w-md">
        <DialogHeader><DialogTitle className="font-display uppercase">Gerar cobrança</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Forma" className="col-span-2">
            <select data-testid="charge-method" value={method} onChange={(e) => setMethod(e.target.value)} className={selectCls}>
              <option value="pix">Pix (QR + copia e cola)</option><option value="boleto">Boleto</option><option value="cartao">Link de cartão de crédito</option>
            </select>
          </Field>
          <Field label="Valor (R$)"><Input data-testid="charge-amount" value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" className={inputCls} /></Field>
          {method === "cartao" && <Field label="Parcelas"><Input data-testid="charge-installments" value={inst} onChange={(e) => setInst(e.target.value.replace(/\D/g, ""))} inputMode="numeric" className={inputCls} /></Field>}
          <p className="col-span-2 text-[11px] text-zinc-500">Quando o cliente pagar, o pagamento entra {source === "sales" ? "na venda" : "na OS"} e a conta a receber recebe baixa sozinha.</p>
          <div className="col-span-2 flex justify-end gap-2">
            <Button variant="outline" className={btnGhost} onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button data-testid="charge-submit" disabled={busy || num(amount) <= 0} onClick={save} className={btnPrimary}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Gerar"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
